import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { parseJson, toJsonText } from '../lib/sportdigiPartner';

/**
 * Digitain yönetim ekranının ortak parçaları.
 *
 * Ekran sağlayıcının kendi hub panelinin yerine geçer ve iki tür alan taşır:
 * **biçimi sabit** olanlar (cashout anahtarı, webhook adresi) ve **katmanlı**
 * olanlar (risk yapılandırması, görünürlük kümesi, tema değişkenleri, marka
 * gövdesi). İkincisi için kutu kutu bir form yazmak sağlayıcının şemasını
 * panele kopyalamak olurdu; sağlayıcı yeni bir alan eklediğinde panel onu
 * tanımaz. Bu yüzden katmanlı gövdeler denetimli bir JSON alanında
 * düzenlenir — dokümanın kendi talimatı da budur: *"read it, change it and
 * send it back complete"*.
 */

export const Panel = ({ title, description, actions, children }) => (
  <section className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
    <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="text-sm font-bold text-white">{title}</h3>
        {description && (
          <p className="mt-1 max-w-3xl text-[11px] leading-relaxed text-slate-400">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
    {children}
  </section>
);

export const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1 text-[11px] text-slate-300">
    <span className="font-semibold uppercase tracking-wide text-slate-400">
      {label}
    </span>
    {children}
    {hint && <span className="text-[10px] text-slate-500">{hint}</span>}
  </label>
);

export const Toggle = ({ label, hint, checked, onChange, disabled }) => (
  <label className="flex items-start gap-2 text-[11px] text-slate-300">
    <input
      type="checkbox"
      className="mt-0.5"
      checked={!!checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span>
      <b className="text-slate-200">{label}</b>
      {hint && <span className="block text-[10px] text-slate-500">{hint}</span>}
    </span>
  </label>
);

export const Busy = ({ children = 'Yükleniyor…' }) => (
  <div className="flex items-center gap-2 py-6 text-[12px] text-slate-400">
    <Loader2 size={14} className="animate-spin" /> {children}
  </div>
);

export const Empty = ({ children }) => (
  <p className="py-6 text-center text-[12px] text-slate-500">{children}</p>
);

export const Problem = ({ children }) => {
  if (!children) return null;
  return (
    <p className="flex items-start gap-2 rounded-lg border border-rose-900/60 bg-rose-950/30 p-2 text-[11px] text-rose-300">
      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
};

/**
 * Sağlayıcının "uygulamadım" listesi.
 *
 * Yazma cevabındaki `ignored_fields` boş değilse kayıt **eksik**
 * uygulanmıştır; sessiz geçmek yöneticiye kurduğunu sandığı bir limitle
 * devam ettirirdi.
 */
export const IgnoredFields = ({ fields }) => {
  if (!fields?.length) return null;
  return (
    <p className="mt-2 rounded-lg border border-amber-900/60 bg-amber-950/30 p-2 text-[11px] text-amber-300">
      <b>Uygulanmayan alanlar:</b> {fields.join(', ')} — sağlayıcı bu
      anahtarları tanımadı, yazım hatası olabilir. Tanınmayan alan sessizce
      atıldığı için kural <u>kurulmuş sayılmaz</u>.
    </p>
  );
};

/**
 * Denetimli JSON alanı.
 *
 * Değer yalnızca **geçerli** olduğunda yukarı verilir: bozuk bir gövdeyi
 * sağlayıcıya göndermek, hatayı panelde değil sağlayıcının 422'sinde
 * görmek demektir.
 */
export const JsonEditor = ({ value, onChange, rows = 14, hint }) => {
  const [text, setText] = useState(() => toJsonText(value));
  const [error, setError] = useState('');

  useEffect(() => {
    setText(toJsonText(value));
    setError('');
  }, [value]);

  const handle = (next) => {
    setText(next);
    const parsed = parseJson(next, {});
    setError(parsed.error);
    if (!parsed.error) onChange(parsed.value);
  };

  return (
    <div className="space-y-1">
      <textarea
        className="admin-input w-full font-mono text-[11px] leading-relaxed"
        rows={rows}
        spellCheck={false}
        value={text}
        onChange={(event) => handle(event.target.value)}
      />
      {hint && !error && (
        <p className="text-[10px] text-slate-500">{hint}</p>
      )}
      <Problem>{error}</Problem>
    </div>
  );
};

export default Panel;
